/* eslint-disable no-sequences */
/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
import classnamesBind from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { withThemeStyles } from '@concur/ui-theming';
import withNuiFormatter from '../utils/withNuiFormatter';
import compose from '../utils/compose';
import AskQuestionMenuItem from './_AskQuestionMenuItem';
import LanguageDropdown from './_LanguageDropdown';
import Menu from './_Menu';
import MenuItem from './_MenuItem';
import Navigation from './_Navigation';
import ProfileMenuItem from './_ProfileMenuItem';
import withErrorBoundary from '../utils/withErrorBoundary';
import * as styles from './AppHeader-*.css';

const CSS_BLOCK = 'sapcnqr-app-header';
const PRIMARY_MENU_ITEM_COUNT_THRESHOLD = 3;

const getPrimaryMenuItemCount = (props) => {
    const {
        menuData,
    } = props;

    const {
        homeMenuItem,
        testDriveMenuItem,
        applicationMenuItems,
    } = menuData;

    let count = 0;
    count += homeMenuItem ? 1 : 0;
    count += testDriveMenuItem ? 1 : 0;
    count += applicationMenuItems ? applicationMenuItems.length : 0;

    return count;
};

const buildUtilityMenu = (props, primaryMenuItemCount) => {
    const {
        menuData,
    } = props;

    const {
        adminMenuItem,
        helpMenuItem,
        linksMenuItem,
        askQuestionMenuItem,
        subscribeMenuItem,
        supportMenuItem,
        localizationToolMenuItem,
    } = menuData;

    const menuItems = [];

    if (subscribeMenuItem) {
        menuItems.push({
            ...subscribeMenuItem,
            anchorClassName: `${CSS_BLOCK}__anchor--tertiary`,
        });
    }

    if (adminMenuItem && primaryMenuItemCount > PRIMARY_MENU_ITEM_COUNT_THRESHOLD) {
        menuItems.push(adminMenuItem);
    }

    if (linksMenuItem && !adminMenuItem && primaryMenuItemCount > PRIMARY_MENU_ITEM_COUNT_THRESHOLD) {
        menuItems.push(linksMenuItem);
    }

    if (supportMenuItem) {
        menuItems.push(supportMenuItem);
    }

    if (helpMenuItem) {
        let helpSubMenuItems = helpMenuItem.subItems || [];
        if (localizationToolMenuItem) {
            helpSubMenuItems = helpSubMenuItems.concat(localizationToolMenuItem);
        }

        menuItems.push({
            ...helpMenuItem,
            subItems: helpSubMenuItems,
        });
    }

    if (askQuestionMenuItem) {
        menuItems.push({
            ...askQuestionMenuItem,
            'data-id': 'question',
        });
    }

    return menuItems;
};

const GatewayHeader = (props) => {
    const {
        actingAs,
        avatarSrc,
        classNameMap,
        formatter,
        fullName,
        isGov,
        isLoggedIn,
        isMayFourth,
        logoutUser,
        menuData,
        onEndSession,
        onStartSession,
        showLanguageDropdown,
        stretchContent,
        useFullWidth,
        useSmallViewportBreakpoints,
    } = props;

    const {
        homeMenuItem,
        applicationMenuItems,
        profileMenuItem,
        adminMenuItem,
        linksMenuItem,
        testDriveMenuItem,
        pageMenuItems,
        noMenuDataFallback,
    } = menuData;

    const classnames = classnamesBind.bind(classNameMap);

    const primaryMenuItemCount = getPrimaryMenuItemCount(props);
    const utilityMenuItems = buildUtilityMenu(props, primaryMenuItemCount);

    const contentRef = React.createRef();
    const utilityRef = React.createRef();

    const classes = classnames(
        CSS_BLOCK,
        {
            [`${CSS_BLOCK}--full-width`]: useFullWidth,
            [`${CSS_BLOCK}--fully-responsive`]: useSmallViewportBreakpoints,
        },
    );

    const primaryContentClasses = classnames(
        `${CSS_BLOCK}__content`,
        `${CSS_BLOCK}__content--primary`,
        { [`${CSS_BLOCK}__content--stretch`]: stretchContent },
    );

    const secondaryContentClasses = classnames(
        `${CSS_BLOCK}__content`,
        `${CSS_BLOCK}__content--secondary`,
        { [`${CSS_BLOCK}__content--stretch`]: stretchContent },
    );

    const primaryNavClasses = classnames(
        `${CSS_BLOCK}__nav`,
        `${CSS_BLOCK}__nav--primary`,
    );

    const utilityNavClasses = classnames(
        `${CSS_BLOCK}__nav`,
        `${CSS_BLOCK}__nav--utility`,
        {
            [`${CSS_BLOCK}__nav--utility-no-menu-data`]: utilityMenuItems.length === 0,
        },
    );

    const secondaryNavClasses = classnames(
        `${CSS_BLOCK}__nav`,
        `${CSS_BLOCK}__nav--secondary`,
    );

    const homeIconClasses = classnames(
        `${CSS_BLOCK}__home-icon`,
        {
            [`${CSS_BLOCK}__home-icon--gov`]: isGov,
        },
    );

    const getWidth = (ref) => ref?.current?.offsetWidth || 0;

    const [utilityWidth, setUtilityWidth] = useState(0);
    const [appHeaderWidth, setAppHeaderWidth] = useState(0);

    const handleResize = () => {
        setUtilityWidth(getWidth(utilityRef));
        setAppHeaderWidth(getWidth(contentRef));
    };
    // runs only on load - this gets us our default utility and container widths
    useEffect(handleResize, []);

    // runs on every resize
    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    });

    return (
        <>
            <div
                className={classnames(classes, `${CSS_BLOCK}--primary`)}
                data-username={fullName || ''}
            >
                {isLoggedIn && (
                    <a
                        className={classnames(`${CSS_BLOCK}__screen-reader-only`)}
                        href="#cnqr-app-content"
                    >
                        {formatter.formattedMessage({ id: 'CoreUI.skipNavigation' })}
                    </a>
                )}
                <div className={primaryContentClasses} ref={contentRef}>
                    {isLoggedIn ? (
                        <Navigation
                            ariaLabel={formatter.formattedMessage({ id: 'CoreUI.primaryMenuAriaLabel' })}
                            className={primaryNavClasses}
                        >
                            <Menu
                                ariaLabel={formatter.formattedMessage({ id: 'CoreUI.primaryMenuAriaLabel' })}
                                className={classnames(`${CSS_BLOCK}__menu`)}
                                collapsible
                                appHeaderWidth={appHeaderWidth}
                                utilityWidth={utilityWidth}
                            >
                                {homeMenuItem && (
                                    <MenuItem
                                        {...homeMenuItem}
                                        classNameMap={classNameMap}
                                        cssBlock={CSS_BLOCK}
                                        anchorClassName={classnames(`${CSS_BLOCK}__anchor--dashboard`)}
                                        isActive={isGov && homeMenuItem.isActive} // home menu item is never shown as active unless Concur Gov
                                        key="dashboard"
                                    >
                                        <i aria-hidden="true" className={homeIconClasses} id="app-header-logo" />
                                    </MenuItem>
                                )}
                                {testDriveMenuItem && (
                                    <MenuItem
                                        {...testDriveMenuItem}
                                        classNameMap={classNameMap}
                                        cssBlock={CSS_BLOCK}
                                        key="testDrive"
                                    />
                                )}
                                {applicationMenuItems && (
                                    applicationMenuItems.map((item, index) => (
                                        <MenuItem
                                            {...item}
                                            classNameMap={classNameMap}
                                            cssBlock={CSS_BLOCK}
                                            hasPopover
                                            key={`application${index}`}
                                        />
                                    ))
                                )}
                                {adminMenuItem && primaryMenuItemCount <= PRIMARY_MENU_ITEM_COUNT_THRESHOLD && (
                                    <MenuItem
                                        {...adminMenuItem}
                                        classNameMap={classNameMap}
                                        cssBlock={CSS_BLOCK}
                                        hasPopover
                                        key="adminMenuItem"
                                    />
                                )}
                                {linksMenuItem && (primaryMenuItemCount <= PRIMARY_MENU_ITEM_COUNT_THRESHOLD || adminMenuItem) && (
                                    <MenuItem
                                        {...linksMenuItem}
                                        classNameMap={classNameMap}
                                        cssBlock={CSS_BLOCK}
                                        hasPopover
                                        key="linksMenuItem"
                                    />
                                )}
                                <MenuItem
                                    classNameMap={classNameMap}
                                    cssBlock={CSS_BLOCK}
                                    id="overflow"
                                    isOverflow
                                    key="overflowPrimary"
                                />
                            </Menu>
                        </Navigation>
                    ) : (
                        <>
                            <span className={classnames(`${CSS_BLOCK}__nav`)}>
                                <span className={classnames(`${CSS_BLOCK}__brand`)}>
                                    <i
                                        aria-label="SAP Concur"
                                        className={homeIconClasses}
                                        role="img"
                                    />
                                </span>
                            </span>
                            {showLanguageDropdown && (
                                <LanguageDropdown
                                    classNameMap={classNameMap}
                                    cssBlock={CSS_BLOCK}
                                    isMayFourth={isMayFourth}
                                />
                            )}
                        </>
                    )}
                    {noMenuDataFallback && React.cloneElement(noMenuDataFallback, {
                        className: classnames(`${CSS_BLOCK}__fallback`),
                        messageClassName: classnames(`${CSS_BLOCK}__fallback-message`),
                    })}
                    {isLoggedIn && (profileMenuItem || (utilityMenuItems.length > 0)) && (
                        <Navigation
                            ariaLabel={formatter.formattedMessage({ id: 'CoreUI.utilityMenuAriaLabel' })}
                            ref={utilityRef}
                            className={utilityNavClasses}
                        >
                            {utilityMenuItems.length > 0 && (
                                <Menu
                                    ariaLabel={formatter.formattedMessage({ id: 'CoreUI.utilityMenuAriaLabel' })}
                                    className={classnames(`${CSS_BLOCK}__menu`)}
                                >
                                    {utilityMenuItems.map((item, index) => (
                                        <React.Fragment key={`fragment${index}`}>
                                            {index !== 0 && (
                                                <MenuItem
                                                    classNameMap={classNameMap}
                                                    cssBlock={CSS_BLOCK}
                                                    key={`pipe${index}`}
                                                    hasPopover
                                                    isPipe
                                                />
                                            )}
                                            {item['data-id'] === 'question'
                                                ? (
                                                    <AskQuestionMenuItem
                                                        {...item}
                                                        classNameMap={classNameMap}
                                                        cssBlock={CSS_BLOCK}
                                                        key={`utility${index}`}
                                                    />
                                                )
                                                : (
                                                    <MenuItem
                                                        {...item}
                                                        classNameMap={classNameMap}
                                                        cssBlock={CSS_BLOCK}
                                                        hasPopover
                                                        isSubItemMenuAlignRight
                                                        key={`utility${index}`}
                                                    />
                                                )}
                                        </React.Fragment>
                                    ))}
                                </Menu>
                            )}
                            {profileMenuItem && (
                                <ProfileMenuItem
                                    actingAs={actingAs}
                                    avatarSrc={avatarSrc}
                                    classNameMap={classNameMap}
                                    cssBlock={CSS_BLOCK}
                                    fullName={fullName}
                                    isGov={isGov}
                                    logoutUser={logoutUser}
                                    onEndSession={onEndSession}
                                    onStartSession={onStartSession}
                                    profileMenuItem={profileMenuItem}
                                />
                            )}
                        </Navigation>
                    )}
                </div>
            </div>
            {isLoggedIn && pageMenuItems && pageMenuItems.length > 0 && (
                <div className={classnames(classes, `${CSS_BLOCK}--secondary`)}>
                    <div className={secondaryContentClasses}>
                        <Navigation
                            ariaLabel={formatter.formattedMessage({ id: 'CoreUI.secondaryMenuAriaLabel' })}
                            className={secondaryNavClasses}
                            id="cnqr-navbar-secondary"
                        >
                            <Menu
                                ariaLabel={formatter.formattedMessage({ id: 'CoreUI.secondaryMenuAriaLabel' })}
                                appHeaderWidth={appHeaderWidth}
                                className={classnames(`${CSS_BLOCK}__menu`)}
                                collapsible
                            >
                                {pageMenuItems.map((item, index) => (
                                    <MenuItem
                                        {...item}
                                        classNameMap={classNameMap}
                                        cssBlock={CSS_BLOCK}
                                        hasPopover
                                        key={`secondary${index}`}
                                    />
                                ))}
                                <MenuItem
                                    classNameMap={classNameMap}
                                    cssBlock={CSS_BLOCK}
                                    id="overflow"
                                    isOverflow
                                    hasPopover
                                    key="overflowSecondary"
                                />
                            </Menu>
                        </Navigation>
                    </div>
                </div>
            )}
        </>
    );
};

GatewayHeader.displayName = 'GatewayHeader';

GatewayHeader.cssBlock = CSS_BLOCK;

GatewayHeader.propTypes = {
    actingAs: PropTypes.shape({
        allUsers: PropTypes.bool,
        companyName: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
        otherUserName: PropTypes.string,
    }),
    avatarSrc: PropTypes.string,
    menuData: PropTypes.shape({
        homeMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        applicationMenuItems: PropTypes.arrayOf(
            PropTypes.shape(MenuItem.apiPropTypes),
        ),
        profileMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        adminMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        helpMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        linksMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        testDriveMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        localizationToolMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        askQuestionMenuItem: PropTypes.shape(AskQuestionMenuItem.propTypes),
        subscribeMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        supportMenuItem: PropTypes.shape(MenuItem.apiPropTypes),
        pageMenuItems: PropTypes.arrayOf(
            PropTypes.shape(MenuItem.apiPropTypes),
        ),
        noMenuDataFallback: PropTypes.node,
    }),
    fullName: PropTypes.string,
    isGov: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    isMayFourth: PropTypes.bool,
    logoutUser: PropTypes.func,
    onEndSession: PropTypes.func,
    onStartSession: PropTypes.func,
    showLanguageDropdown: PropTypes.bool,
    stretchContent: PropTypes.bool,
    useFullWidth: PropTypes.bool,
    useSmallViewportBreakpoints: PropTypes.bool,
};

export default withThemeStyles(compose(
    withNuiFormatter,
    withErrorBoundary,
)(GatewayHeader), styles);
